import { FirebaseError } from "firebase/app";

type AuthErrorTranslate = (key: string, fallback: string) => string;

type AuthErrorContext = "signIn" | "signUp" | "passwordReset" | "resend";

const FALLBACKS: Record<
  AuthErrorContext,
  { userNotFound: string; wrongPassword?: string; invalidCredential?: string; emailInUse?: string; generic: string }
> = {
  signIn: {
    userNotFound: "We couldn't find an account for that email. Check the address or sign up instead.",
    wrongPassword: "That password doesn't match this email. Try again.",
    invalidCredential: "We couldn't sign you in with that email and password. Check them and try again.",
    generic: "An unexpected error occurred during sign-in.",
  },
  signUp: {
    userNotFound: "We couldn't find an account for that email. Check the address or sign up instead.",
    emailInUse: "That email is already registered. Try signing in instead.",
    generic: "An unexpected error occurred during sign-up.",
  },
  passwordReset: {
    userNotFound: "We couldn't find an account for that email. Check the address or sign up instead.",
    generic: "Something went wrong. Please try again later.",
  },
  resend: {
    userNotFound: "",
    generic: "Failed to send. You may need to wait a minute before trying again.",
  },
};

const I18N_KEYS: Record<
  AuthErrorContext,
  { userNotFound: string; wrongPassword?: string; invalidCredential?: string; emailInUse?: string; generic: string }
> = {
  signIn: {
    userNotFound: "login.errors.userNotFound",
    wrongPassword: "login.errors.wrongPassword",
    invalidCredential: "login.errors.invalidCredential",
    generic: "login.errors.unexpectedSignIn",
  },
  signUp: {
    userNotFound: "login.errors.userNotFound",
    emailInUse: "login.errors.emailInUse",
    generic: "login.errors.unexpectedSignUp",
  },
  passwordReset: {
    userNotFound: "login.errors.userNotFound",
    generic: "login.forgotPassword.failed",
  },
  resend: {
    userNotFound: "",
    generic: "login.verifyEmail.resendFailed",
  },
};

/**
 * Maps an auth error (e.g. FirebaseError) to a user-facing message using the given translate function.
 */
export function mapAuthErrorToMessage(error: unknown, tr: AuthErrorTranslate, context: AuthErrorContext): string {
  const keys = I18N_KEYS[context];
  const fallbacks = FALLBACKS[context];

  if (error instanceof FirebaseError) {
    switch (error.code) {
      case "auth/user-not-found":
        return keys.userNotFound ? tr(keys.userNotFound, fallbacks.userNotFound) : fallbacks.generic;
      case "auth/wrong-password":
        if (context === "signIn" && keys.wrongPassword && fallbacks.wrongPassword) {
          return tr(keys.wrongPassword, fallbacks.wrongPassword);
        }
        return tr(keys.generic, fallbacks.generic);
      case "auth/invalid-credential":
        // Identity Platform's email-enumeration protection collapses auth/user-not-found and
        // auth/wrong-password into this single code on sign-in.
        if (context === "signIn" && keys.invalidCredential && fallbacks.invalidCredential) {
          return tr(keys.invalidCredential, fallbacks.invalidCredential);
        }
        return tr(keys.generic, fallbacks.generic);
      case "auth/email-already-in-use":
        if (context === "signUp" && keys.emailInUse && fallbacks.emailInUse) {
          return tr(keys.emailInUse, fallbacks.emailInUse);
        }
        return tr(keys.generic, fallbacks.generic);
      default:
        return tr(keys.generic, fallbacks.generic);
    }
  }

  if (error instanceof Error && context === "resend") {
    return error.message;
  }

  return tr(keys.generic, fallbacks.generic);
}
